import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useCourseDetailModal, CourseDetailModal } from '../../components/CourseDetailModal';
import { courseToTheme } from '../../data/divisionThemeVisuals';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowRight, Microscope, Trophy, Users, Atom, Heart, Dna, Brain, Award,
  Leaf, Stethoscope, FlaskConical, Telescope, CheckCircle2, ChevronDown,
  Send, Sparkles, GraduationCap, Beaker, Eye, Bug, Flower2, Activity,
  Pill, Shield, Syringe, BookOpen, Target, MessageSquare, Check, Star,
  Fingerprint, Search, Zap, Clock, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── Contact Form ───── */
function InlineContactForm({ program }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', studentGrade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { toast.error('Name and email required.'); return; }
    setSubmitting(true);
    try { await fetch(`${API_URL}/api/contact/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, subject: `${program} Interest`, message: `Program: ${program}\nGrade: ${formData.studentGrade}` }) }); } catch {}
    setSubmitted(true); setSubmitting(false);
    toast.success("We received your interest! We'll be in touch soon.");
  };

  if (submitted) return (
    <div className="text-center py-3">
      <CheckCircle2 className="w-7 h-7 text-emerald-400 mx-auto mb-1" />
      <p className="text-xs font-semibold text-white">Received! We'll be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" placeholder="Name *" value={formData.name} onChange={e => setFormData(p=>({...p, name: e.target.value}))} required />
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData(p=>({...p, email: e.target.value}))} required />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" placeholder="Phone" value={formData.phone} onChange={e => setFormData(p=>({...p, phone: e.target.value}))} />
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" placeholder="Student Grade" value={formData.studentGrade} onChange={e => setFormData(p=>({...p, studentGrade: e.target.value}))} />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-rsc-yellow text-rsc-navy font-bold rounded-full h-8 text-xs">
        {submitting ? 'Sending...' : <><Send className="w-3 h-3 mr-1" /> Express Interest</>}
      </Button>
    </form>
  );
}

/* ───── Career Pathway Data ───── */
const CAREER_PATHS = [
  { title: 'Doctor / Physician', salary: '$229K+', icon: Stethoscope, prep: 'Anatomy, Physiology, Chemistry, Biology' },
  { title: 'Pharmacist', salary: '$132K+', icon: Pill, prep: 'Chemistry, Genetics, Physiology' },
  { title: 'Biomedical Engineer', salary: '$100K+', icon: Activity, prep: 'Physics, Biology, Chemistry, Math' },
  { title: 'Genetic Counselor', salary: '$95K+', icon: Dna, prep: 'Genetics, Biology, Psychology' },
  { title: 'Wildlife Biologist', salary: '$73K+', icon: Bug, prep: 'Zoology, Ecology, Botany, Statistics' },
  { title: 'Research Scientist', salary: '$90K+', icon: Microscope, prep: 'All Sciences, Lab Skills, Data Analysis' },
];

const HERO_STATS = [
  { value: '6', label: 'Themed Science Programs', icon: FlaskConical },
  { value: 'Gr 3–8', label: 'Core Audience', icon: Users },
  { value: '100%', label: 'Hands-On with Kits', icon: Beaker },
  { value: 'Top 5%', label: 'STEM.org Accredited', icon: Award },
];

/* ───── Main Page ───── */
export default function ScienceDivisionPage() {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [themes, setThemes] = useState([]);           // DB-driven; shape comes from courseToTheme()
  const [loadingThemes, setLoadingThemes] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && hash !== 'categories') {
      setExpandedCategory(hash);
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
    }
  }, [location.hash]);

  useEffect(() => {
    setLoadingThemes(true);
    axios.get(`${API_URL}/api/courses/?category=science&page_size=100`).then(res => {
      const courses = (res.data?.results || res.data || [])
        .filter(c => c.active !== false && c.category === 'science')
        // Deduplicate by title (Summer camp weeks repeat course titles)
        .filter((c, i, arr) => arr.findIndex(x => x.title === c.title) === i);
      setThemes(courses.map(courseToTheme));
    }).catch(() => setThemes([])).finally(() => setLoadingThemes(false));
  }, []);

  const courseModal = useCourseDetailModal();

  return (
    <div data-testid="science-division-page">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847]">
        {/* Grid overlay — yellow tint to match site theme */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        {/* Glow effects */}
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] bg-orange-500/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-10 -left-40 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />

        <div className="container-app relative z-10 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-4" data-testid="hero-badge">
                <Stethoscope className="w-4 h-4" /> From Lab Coats to White Coats
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-4 tracking-tight">
                Every Doctor Started<br />
                <span className="text-rsc-yellow">as a Curious Kid.</span>
              </h1>
              <p className="text-base sm:text-lg text-white/60 max-w-xl mb-7 leading-relaxed">
                Hands-on science kits, real lab techniques, and themed adventures
                that build the foundation for medicine, research, and every science career.
                Designed for grades 3–8 — where curiosity becomes capability.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/catalog?category=science">
                  <Button className="bg-rsc-yellow text-rsc-navy text-sm px-7 py-5 font-bold rounded-full shadow-lg shadow-rsc-yellow/20 hover:shadow-rsc-yellow/30 hover:scale-[1.02] transition-all" data-testid="explore-courses-btn">
                    Explore Science Courses <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="#categories">
                  <Button variant="outline" className="border-white/25 text-white hover:bg-white/10 text-sm px-7 py-5 rounded-full" data-testid="view-themes-btn">
                    Browse Themes
                  </Button>
                </a>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-stats">
                {HERO_STATS.map((stat, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white/[0.06] border border-white/[0.08]">
                    <stat.icon className="w-4 h-4 text-rsc-yellow/60 mb-1" />
                    <p className="text-xl font-extrabold text-white leading-none">{stat.value}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero image collage */}
            <div className="hidden lg:grid grid-cols-2 gap-3">
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden aspect-[4/3]">
                  <img src="https://images.pexels.com/photos/5427861/pexels-photo-5427861.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Kids science experiment" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/5]">
                  <img src="https://images.pexels.com/photos/5063470/pexels-photo-5063470.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Student using microscope" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="space-y-3 pt-6">
                <div className="rounded-xl overflow-hidden aspect-[4/5]">
                  <img src="https://images.unsplash.com/photo-1758685734153-132c8620c1bd?w=400&auto=format" alt="Kids in lab coats" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3]">
                  <img src="https://images.pexels.com/photos/7606123/pexels-photo-7606123.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Science learning activity" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/20" />
        </div>
      </section>

      {/* ═══════════ THEMED SCIENCE CATEGORIES ═══════════ */}
      <section id="categories" className="relative py-12 bg-gradient-to-b from-[#0f2847] to-rsc-navy" data-testid="themed-categories-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" /> THEMED ADVENTURES
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              Science That Feels Like an <span className="text-rsc-yellow">Adventure</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto text-sm">
              Every program comes with a hands-on kit. Every lesson starts with a mystery to solve.
              Real science, wrapped in themes kids can't resist.
            </p>
          </div>

          <div className="space-y-3">
            {loadingThemes && (
              <div className="flex items-center justify-center py-12 text-white/40">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading themes…
              </div>
            )}
            {!loadingThemes && themes.length === 0 && (
              <p className="text-center text-white/50 py-8 text-sm">No science courses found. Check back soon!</p>
            )}
            {themes.map((cat) => {
              const Icon = cat.icon;
              const isExpanded = true;
              return (
                <div key={cat.id} id={cat.id} data-testid={`theme-${cat.id}`}>
                  <div
                    className={`rounded-xl border transition-all border-white/15 bg-white/[0.05]`}
                    
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`relative w-11 h-11 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center shadow-lg flex-shrink-0 overflow-hidden`}>
                          {cat.thumbnailUrl ? (
                            <>
                              <img src={cat.thumbnailUrl} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                              <div className={`absolute inset-0 bg-gradient-to-br ${cat.color} opacity-70 mix-blend-multiply`} />
                              <Icon className="relative w-5 h-5 text-white drop-shadow" />
                            </>
                          ) : (
                            <Icon className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <h3 className="text-lg font-bold text-white">{cat.theme}</h3>
                              <p className={`text-xs ${cat.colorAccent} font-medium`}>{cat.tagline} · {cat.grades}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="hidden sm:flex gap-1">
                                {cat.tags.map(t => <Badge key={t} className={`${cat.bgAccent} ${cat.colorAccent} text-[10px]`}>{t}</Badge>)}
                              </div>
                              <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                          {!isExpanded && (
                            <p className="text-xs text-white/35 mt-1.5 line-clamp-1">{cat.description}</p>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-5 ml-0 sm:ml-[60px] space-y-4" data-testid={`theme-detail-${cat.id}`}>
                          <p className="text-sm text-white/50 leading-relaxed">{cat.description}</p>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Sample Activities</h4>
                              <div className="space-y-1">
                                {cat.sampleActivities.map((a, j) => (
                                  <div key={j} className="flex items-start gap-1.5">
                                    <Check className={`w-3.5 h-3.5 ${cat.colorAccent} flex-shrink-0 mt-0.5`} />
                                    <span className="text-xs text-white/50">{a}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">What's in the Kit</h4>
                              <div className="space-y-1">
                                {cat.kitItems.map((k, j) => (
                                  <div key={j} className="flex items-start gap-1.5">
                                    <Beaker className={`w-3.5 h-3.5 ${cat.colorAccent} flex-shrink-0 mt-0.5`} />
                                    <span className="text-xs text-white/50">{k}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 pt-1">
                            <Button size="sm" className={`bg-gradient-to-r ${cat.color} text-white font-bold rounded-full px-5 cursor-pointer`} onClick={() => courseModal.openCourseDetail(cat.theme)}>
                              Course Details <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                            </Button>
                            {activeForm !== cat.id ? (
                              <Button size="sm" variant="outline" className="border-white/15 text-white/60 hover:bg-white/5 rounded-full px-5" onClick={(e) => { e.stopPropagation(); setActiveForm(cat.id); }}>
                                <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> I'm Interested
                              </Button>
                            ) : (
                              <div className="flex-1 max-w-sm" onClick={e => e.stopPropagation()}>
                                <InlineContactForm program={cat.theme} />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ CAREER OUTCOMES ═══════════ */}
      <section className="relative py-12 bg-rsc-navy" data-testid="career-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-orange/30 bg-rsc-orange/10 text-rsc-orange text-xs font-medium mb-3">
              <GraduationCap className="w-3.5 h-3.5" /> WHERE SCIENCE LEADS
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              From Curious Kid to <span className="text-rsc-yellow">Career</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto text-sm">
              Every experiment today is a building block for tomorrow's career. Here's where the science pathway leads.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {CAREER_PATHS.map((career, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.06] text-center hover:bg-white/[0.07] transition-colors" data-testid={`career-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-rsc-yellow/10 flex items-center justify-center mx-auto mb-2">
                  <career.icon className="w-5 h-5 text-rsc-yellow" />
                </div>
                <h3 className="text-sm font-bold text-white mb-0.5">{career.title}</h3>
                <p className="text-rsc-yellow font-bold text-sm mb-1">{career.salary}</p>
                <p className="text-[10px] text-white/30">{career.prep}</p>
              </div>
            ))}
          </div>

          {/* AP & Science Olympiad Pathways */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-indigo-500/[0.08] border border-indigo-500/20" data-testid="ap-pathway">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">AP Science Prep Pathway</h3>
                  <p className="text-xs text-indigo-300/60">Build the foundation in grades 3–8, ace AP exams in high school</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { ap: 'AP Biology', prep: 'Gene Lab + Nature Explorers' },
                  { ap: 'AP Chemistry', prep: 'Chemistry Kitchen + Medical Detectives' },
                  { ap: 'AP Physics', prep: 'Space & Physics + Engineering' },
                  { ap: 'AP Environmental', prep: 'Nature Explorers + Ecology' },
                ].map((item, i) => (
                  <div key={i} className="p-2 rounded-lg bg-white/[0.04] border border-white/[0.04]">
                    <p className="text-xs font-semibold text-white">{item.ap}</p>
                    <p className="text-[10px] text-white/30">{item.prep}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 rounded-xl bg-amber-500/[0.08] border border-amber-500/20" data-testid="olympiad-pathway">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Science Olympiad Prep</h3>
                  <p className="text-xs text-amber-300/60">Our courses directly map to Science Olympiad event categories</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { event: 'Anatomy & Physiology', prep: 'Body Builders' },
                  { event: 'Disease Detectives', prep: 'Medical Detectives' },
                  { event: 'Heredity', prep: 'Gene Lab' },
                  { event: 'Ecology / Green Generation', prep: 'Nature Explorers' },
                  { event: 'Chemistry Lab', prep: 'Chemistry Kitchen' },
                  { event: 'Reach for the Stars', prep: 'Space & Physics' },
                ].map((item, i) => (
                  <div key={i} className="p-2 rounded-lg bg-white/[0.04] border border-white/[0.04]">
                    <p className="text-xs font-semibold text-white">{item.event}</p>
                    <p className="text-[10px] text-white/30">{item.prep}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ HANDS-ON KIT PROMISE ═══════════ */}
      <section className="relative py-12 bg-gradient-to-b from-rsc-navy to-[#0f2847]" data-testid="kit-promise-section">
        <div className="container-app">
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-8 sm:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
                  <Beaker className="w-3.5 h-3.5" /> OUR PROMISE
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
                  Every Course. A <span className="text-rsc-yellow">Real Kit.</span>
                </h2>
                <p className="text-sm text-white/50 mb-5 leading-relaxed">
                  No worksheets. No passive screen time. Every Ready Set STEM science course ships with a
                  hands-on kit — real lab equipment, real specimens, real experiments.
                  Kids learn by doing, just like real scientists.
                </p>
                <div className="space-y-2">
                  {[
                    { icon: Beaker, text: 'Professional-grade lab equipment (kid-safe)' },
                    { icon: Clock, text: 'Multi-session kits that build on each lesson' },
                    { icon: BookOpen, text: 'Parent guide + instructor-led lessons' },
                    { icon: Shield, text: 'STEM.org accredited curriculum (Top 5%)' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <item.icon className="w-4 h-4 text-rsc-yellow flex-shrink-0" />
                      <span className="text-sm text-white/60">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Test Tubes & Beakers', icon: FlaskConical, color: 'bg-blue-500/15 text-blue-400' },
                  { label: 'Stethoscopes & Models', icon: Stethoscope, color: 'bg-rose-500/15 text-rose-400' },
                  { label: 'Magnifiers & Scopes', icon: Eye, color: 'bg-green-500/15 text-green-400' },
                  { label: 'DNA Extraction Kits', icon: Dna, color: 'bg-purple-500/15 text-purple-400' },
                  { label: 'Prisms & Magnets', icon: Atom, color: 'bg-indigo-500/15 text-indigo-400' },
                  { label: 'Safety Goggles', icon: Shield, color: 'bg-amber-500/15 text-amber-400' },
                ].map((kit, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${kit.color.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                      <kit.icon className={`w-4 h-4 ${kit.color.split(' ')[1]}`} />
                    </div>
                    <span className="text-xs text-white/50 font-medium">{kit.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ BOTTOM CTA ═══════════ */}
      <section className="relative py-12 bg-[#0f2847]">
        <div className="container-app text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
            Spark the curiosity. Build the future doctor.
          </h2>
          <p className="text-sm text-white/45 mb-5 max-w-md mx-auto">
            Hands-on science adventures for grades 3–8. Real kits. Real experiments. Real career foundations.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/catalog?category=science">
              <Button className="bg-rsc-yellow text-rsc-navy text-sm px-8 py-5 font-bold rounded-full shadow-lg shadow-rsc-yellow/20" data-testid="bottom-cta-courses">
                Explore Courses <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#categories">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 text-sm px-8 py-5 rounded-full" data-testid="bottom-cta-themes">
                Browse Themes
              </Button>
            </a>
          </div>
        </div>
      </section>
      <CourseDetailModal state={courseModal.state} onClose={courseModal.close} onRegister={(course) => window.location.href = `/summer-camps?register=${encodeURIComponent(course.title)}`} />
    </div>
  );
}
